import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, RefreshCw } from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { useT } from "../i18n";
import {
  ChipEvent,
  DayGroup,
  LIST_DAYS,
  groupUpcoming,
  isImminent,
  listWindowEnd,
  meetingLabel,
  ortLabel,
  pickNextMeeting,
  timeLabel,
} from "./nextMeeting";

// X2 — Termin-Anzeige im Atlas-Kopf. Liest den eigenen Kalender read-only über
// das bestehende GET /calendar/view (kein neuer Egress). Auswahl-/Gruppier-/
// Label-Logik liegt JSX-frei in ./nextMeeting.ts (dort im Dry-Run testbar).
//
// 2026-08-07 (Rückmeldung Tim Bentler): Aus dem einzeiligen „Nächster Termin"-
// Chip ist in der Desktop-Sidebar eine TERMINLISTE über 7 Tage geworden —
// tagesweise gruppiert wie die Outlook-Terminliste, ~4-5 Zeilen sichtbar, der
// Rest scrollbar. Sein zweiter Punkt („zeigt nur 14:00, obwohl 10:30 und 11:00
// auch anstehen") war KEIN Sync-Problem: es gibt keinen Sync-Job und keinen
// Cache, Graph wird live gelesen — der Chip hat nur alle 120 s nachgeladen und
// zeigte genau EINEN Termin. Deshalb hier zusätzlich ein Refetch beim
// Zurückkehren ins Fenster (window-focus).

export type { ChipEvent };

const POLL_MS = 120_000; // Refetch-Takt, visibility-gated (Muster Mail.tsx:7150-7180)
const TICK_MS = 30_000; // Countdown-/Tagesgrenzen-Neuzeichnen OHNE Refetch (Muster Mail.tsx:3406)
// Fokus-Refetch: mindestens so viel Abstand zum letzten Laden. Ohne die Bremse
// löst jedes Alt-Tab einen Graph-Durchgriff aus (der Endpunkt hat keinen Cache).
const MIN_REFETCH_MS = 15_000;
// Relevanz-Fenster der KOMPAKTEN Mobil-Variante. Die Liste holt 7 Tage; ein
// „um 08:30" in der Kopfzeile darf aber nicht heimlich der Termin von Montag
// sein — mobil bleibt es deshalb beim bisherigen Ausblick von ~12 h.
const MOBILE_HORIZON_MS = 12 * 60 * 60 * 1000;

/**
 * Termin-Anzeige. Zwei Erscheinungsformen (D1):
 *   - variant="desktop": 7-Tage-Terminliste im Sidebar-Kopf (nur >=md).
 *   - variant="mobile":  kompakt (Icon + Countdown, OHNE Titel) in der Mobile-Top-Bar.
 * (Die dritte Form, `variant="schiene"`, gehörte zur eingeklappten 56px-Leiste
 * und ist mit ihr entfallen — #78, 2026-07-31.)
 * Fetch-Fehler → die Komponente rendert `null` (Ruhe durch Subtraktion, D5):
 * kein roter Zustand, kein Toast, kein Dialog. „Geladen, aber nichts drin" ist
 * dagegen eine Auskunft und bekommt in der Liste eine ruhige Zeile.
 */
export function NextMeetingChip({
  variant = "desktop",
}: {
  variant?: "desktop" | "mobile";
}) {
  // `null` = noch nicht geladen ODER Fehler → nichts rendern (kein Skelett, das
  // ewig pulsiert). `[]` = geladen und leer → Leer-Zeile (Desktop).
  const [events, setEvents] = useState<ChipEvent[] | null>(null);
  // F-31 (Tim, 10.08.2026: „wann aktualisiert sie sich?") — Zeitpunkt des
  // letzten ERFOLGREICHEN Ladens. Die Frage wird dort beantwortet, wo sie
  // entsteht: in der Liste selbst, statt in einer Doku, die niemand liest.
  const [geladenUm, setGeladenUm] = useState<Date | null>(null);
  // Letzter Versuch fehlgeschlagen? Nur damit der Aktualisieren-Knopf nicht
  // lügt: ohne dieses Bit sähe ein erfolgloser Klick genauso aus wie ein
  // erfolgreicher (Liste bleibt stehen, Uhrzeit bleibt stehen).
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false);
  const [laedt, setLaedt] = useState(false);
  // Reines Neuzeichnen (Countdown, Tageswechsel, abgelaufene Zeilen fallen raus):
  // bump → Re-Render → `new Date()` neu berechnet.
  const [, setTick] = useState(0);
  const lastLoadRef = useRef(0);
  const montiert = useRef(true);
  // Die Beschriftungen der Countdown-/Tages-Logik werden an die katalogfreien
  // Funktionen aus ./nextMeeting durchgereicht (Begründung dort im Kopf).
  const t = useT();
  const texte = t.shared.termine;

  // EIN Ladeweg für alle vier Auslöser (Erstfetch · 120-s-Takt · window-focus ·
  // Aktualisieren-Knopf). Zwei Ladefunktionen wären zwei Wahrheiten darüber,
  // wann `geladenUm` fortschreibt — und genau diese Uhrzeit ist die Auskunft.
  const load = useCallback(async () => {
    const now = new Date();
    lastLoadRef.current = now.getTime();
    setLaedt(true);
    const end = listWindowEnd(now);
    try {
      const res = await api.get<{ events?: ChipEvent[] }>(
        `/calendar/view?start=${encodeURIComponent(
          now.toISOString()
        )}&end=${encodeURIComponent(end.toISOString())}`
      );
      if (!montiert.current) return;
      setEvents(res?.events ?? []);
      setGeladenUm(new Date());
      setFehlgeschlagen(false);
    } catch {
      // Erst-Fehler (noch nie geladen) → `events` bleibt `null` → die Komponente
      // rendert nichts (Ruhe durch Subtraktion, D5 — unverändert).
      // Steht dagegen schon eine Liste, bleibt sie STEHEN. Sie wegzuwerfen hieße,
      // auf einen Klick des Nutzers hin die Anzeige zu leeren; dass sie alt ist,
      // sagt jetzt die Stand-Zeile — genau dafür ist sie da.
      if (montiert.current) setFehlgeschlagen(true);
    } finally {
      if (montiert.current) setLaedt(false);
    }
  }, []);

  // Visibility-gated Poll (Muster Mail.tsx:7150-7180): nur bei sichtbarem Tab
  // laden; bei Rückkehr zu „visible" sofort einmal nachladen + Interval starten,
  // sonst stoppen. Zusätzlich beim window-focus (Tim: „gibt es feste Sync-Zeiten?"
  // — nein, aber der Blick in die Liste soll frisch sein).
  useEffect(() => {
    montiert.current = true;
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (document.visibilityState === "visible") void load();
      }, POLL_MS);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void load();
        start();
      } else {
        stop();
      }
    };
    const onFocus = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastLoadRef.current < MIN_REFETCH_MS) return;
      void load();
    };

    if (document.visibilityState === "visible") {
      void load(); // initialer Fetch
      start();
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      montiert.current = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  // Separater 30-s-Tick NUR fürs Neuzeichnen (kein Refetch, D4).
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), TICK_MS);
    return () => clearInterval(t);
  }, []);

  if (!events) return null; // noch nicht geladen / Fehler → nichts
  const now = new Date();

  if (variant === "mobile") {
    // Kompakt: Icon + Countdown, OHNE Titel (kein Crowding in der Mobile-Top-Bar).
    const next = pickNextMeeting(events, now);
    if (!next) return null;
    if (new Date(next.start).getTime() - now.getTime() > MOBILE_HORIZON_MS) {
      return null; // erst ab dem 12-h-Ausblick relevant (s. MOBILE_HORIZON_MS)
    }
    const label = meetingLabel(next, now, texte);
    const joinUrl = next.join_url && next.join_url.trim() ? next.join_url : null;
    return (
      <div
        data-next-meeting-chip
        data-variant="mobile"
        className="inline-flex items-center gap-1.5 rounded-lg border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 px-2 py-1 text-xs"
      >
        <Link
          to="/calendar"
          aria-label={texte.naechsterTermin(next.subject, label)}
          title={`${next.subject} · ${label}`}
          className="inline-flex items-center gap-1.5 text-ink-50/80 hover:text-ink transition-colors"
        >
          <CalendarClock className="w-3.5 h-3.5 shrink-0 text-ink-50/60" />
          <span data-chip-countdown className="whitespace-nowrap tabular-nums">
            {label}
          </span>
        </Link>
        {joinUrl && (
          <a
            data-chip-join
            href={joinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap font-medium text-brand-600 hover:text-brand-700 transition-colors"
          >
            {texte.beitreten}
          </a>
        )}
      </div>
    );
  }

  // Desktop: Terminliste im Sidebar-Kopf (nur >=md sichtbar).
  const groups: DayGroup[] = groupUpcoming(events, now, texte);

  return (
    <div
      data-next-meeting-chip
      data-variant="desktop"
      className="hidden md:block mx-3 mt-3 rounded-lg border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800"
    >
      {/* F-31 — Stand + Aktualisieren. Die Liste lädt beim Öffnen, alle 120 s
          und beim Zurückkehren ins Fenster; das sah man ihr aber nicht an, und
          wer gerade in Outlook einen Termin angelegt hatte, konnte nicht
          unterscheiden zwischen „ist noch nicht da" und „wird nie geholt".
          Die Uhrzeit beantwortet das, der Knopf verkürzt das Warten. */}
      <div className="flex items-center justify-between gap-2 border-b border-cream-200 dark:border-ink-600 px-2.5 py-1 text-[11px] text-ink-50/45">
        <span data-termine-stand>
          {geladenUm
            ? texte.standUm(
                geladenUm.toLocaleTimeString("de-DE", {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              )
            : texte.standUnbekannt}
          {fehlgeschlagen && texte.nichtErreichbar}
        </span>
        <button
          type="button"
          data-termine-aktualisieren
          onClick={() => void load()}
          disabled={laedt}
          aria-label={texte.aktualisieren}
          title={texte.aktualisieren}
          className="shrink-0 rounded p-0.5 text-ink-50/45 hover:text-ink hover:bg-cream-100 dark:hover:bg-ink-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={clsx("h-3 w-3", laedt && "animate-spin")} />
        </button>
      </div>
      {groups.length === 0 ? (
        // Geladen, aber nichts da: EINE ruhige Zeile (kein leerer Kasten, kein
        // Skelett) — und sie bleibt der Weg in den Kalender.
        <Link
          to="/calendar"
          data-termine-leer
          className="flex items-start gap-1.5 px-2.5 py-2 text-xs leading-snug text-ink-50/60 hover:text-ink transition-colors"
        >
          <CalendarClock className="mt-px w-3.5 h-3.5 shrink-0 text-ink-50/45" />
          <span>{texte.keineTermine(LIST_DAYS)}</span>
        </Link>
      ) : (
        <div
          data-termine-liste
          aria-label={texte.listeAria(LIST_DAYS)}
          // 15rem ≈ 5 volle Zeilen + angeschnittene sechste: Tim wollte „4-5
          // Einträge untereinander" UND einen sichtbaren Hinweis, dass unten
          // noch etwas kommt. Die Navigation darunter scrollt bei Bedarf selbst.
          className="max-h-60 overflow-y-auto overscroll-contain py-1"
        >
          {groups.map((group) => (
            <div key={group.key} data-tag-gruppe={group.key}>
              <div
                data-tag-label
                className="px-2.5 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-50/45"
              >
                {group.label}
              </div>
              {group.events.map((ev, i) => {
                const imminent = isImminent(ev, now);
                const label = meetingLabel(ev, now, texte); // „läuft" / „in n Min"
                const joinUrl =
                  ev.join_url && ev.join_url.trim() ? ev.join_url : null;
                const showJoin = imminent && !!joinUrl;
                const ort = ortLabel(ev);
                // Zweite Zeile nur, wenn sie etwas zu sagen hat (Content-First):
                // jetzt-relevant → Countdown (+ Beitreten), sonst der Ort.
                const zweiteZeile = imminent || !!ort;
                return (
                  <div
                    key={`${group.key}-${i}`}
                    data-termin-eintrag
                    className="flex gap-1.5 px-2.5 py-1 transition-colors hover:bg-cream-100 dark:hover:bg-ink-700/60"
                  >
                    <span
                      data-termin-zeit
                      className="w-9 shrink-0 pt-px text-[11px] leading-tight tabular-nums text-ink-50/60"
                    >
                      {timeLabel(ev)}
                    </span>
                    <span
                      aria-hidden
                      className={clsx(
                        "w-[3px] shrink-0 rounded-full",
                        imminent
                          ? "bg-brand-600"
                          : "bg-brand-400/80 dark:bg-brand-700"
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <Link
                        to="/calendar"
                        data-chip-title
                        title={`${timeLabel(ev)} · ${ev.subject}${
                          ort ? ` · ${ort}` : ""
                        }`}
                        className="block truncate text-[12px] font-medium leading-tight text-ink transition-colors hover:text-brand-600"
                      >
                        {ev.subject}
                      </Link>
                      {zweiteZeile && (
                        <div className="flex items-center gap-1 text-[11px] leading-tight">
                          {imminent && (
                            <span
                              data-chip-countdown
                              className="shrink-0 whitespace-nowrap font-medium text-brand-600"
                            >
                              {label}
                            </span>
                          )}
                          {showJoin && (
                            <span aria-hidden className="text-ink-50/35">
                              ·
                            </span>
                          )}
                          {showJoin && (
                            <a
                              data-chip-join
                              href={joinUrl as string}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 whitespace-nowrap font-medium text-brand-600 transition-colors hover:text-brand-700"
                            >
                              {texte.beitreten}
                            </a>
                          )}
                          {!imminent && ort && (
                            <span
                              data-termin-ort
                              className="truncate text-ink-50/50"
                            >
                              {ort}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default NextMeetingChip;
